import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "src/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "src/components/ui/card";
import { Input } from "src/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import { Save, Briefcase, Star, ArrowRight } from "lucide-react";
import { DatePicker, theme } from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import { useTheme } from "next-themes";
import { Slider } from "antd";

// Import Ant Design CSS
import "antd/dist/reset.css";
import { useNavigate, useParams } from "react-router-dom";
import { BaseUrlApi, ErrorMessage } from "../lib/api";
import axios from "axios";
import { toast } from "sonner";
import { Skeleton } from "src/components/ui/skeleton";

// Configure dayjs locale
dayjs.locale("ar");

export default function EditProject() {
  const navigate = useNavigate();

  const { bid } = useParams();

  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    category_id: "",
    date_start: "",
    date_end: "",
    progress: 0,
    status: "in_progress",
    review: 0,
    team_id: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleProgressChange = (value) => {
    setFormData((prev) => ({ ...prev, progress: value }));
  };

  const handleReviewChange = (value) => {
    setFormData((prev) => ({ ...prev, review: Number.parseInt(value) }));
  };

  const handleStartDateChange = (date) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date_start: date.format("YYYY-MM-DD HH:mm:ss"),
      }));
    }
  };

  const handleEndDateChange = (date) => {
    if (date) {
      setFormData((prev) => ({
        ...prev,
        date_end: date.format("YYYY-MM-DD HH:mm:ss"),
      }));
    }
  };

  // Project
  const [loadingProject, setLoadingProject] = useState(false);
  const handleFetchProject = async () => {
    try {
      setLoadingProject(true);
      const { data } = await axios.get(`${BaseUrlApi}/projects/${bid}`);
      console.log(data.data.category.id);
      setFormData({
        name: data.data?.name,
        category_id: data.data?.category.id,
        date_start: data.data?.date_start,
        date_end: data.data?.date_end,
        progress: data.data?.progress,
        status: data.data?.status,
        review: data.data?.review,
        team_id: data.data?.team.id,
      });
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
    } finally {
      setLoadingProject(false);
    }
  };

  // Categories
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [Categories, setCategories] = useState([]);
  const handleFetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const { data } = await axios.get(`${BaseUrlApi}/categories`);
      setCategories(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حذث خطأ !", { description: result });
    } finally {
      setLoadingCategories(false);
    }
  };

  // Teams
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [Teams, setTeams] = useState([]);
  const handleFetchTeams = async () => {
    try {
      setLoadingTeams(true);
      const { data } = await axios.get(`${BaseUrlApi}/teams`);
      setTeams(data.data);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("حذث خطأ !", { description: result });
    } finally {
      setLoadingTeams(false);
    }
  };

  useEffect(() => {
    if (bid) {
      handleFetchProject();
      handleFetchTeams();
      handleFetchCategories();
    }
  }, [bid]);

  const [errors, setErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      await axios.put(`${BaseUrlApi}/projects/${bid}`, formData);
      toast("تم التعديل", {
        description: "تم تحديث تفاصيل المشروع بنجاح ✏️",
      });

      navigate(-1);
    } catch (error) {
      const result = ErrorMessage(error);
      if (typeof result !== "object")
        toast("Error", {
          description: result,
        });
      setErrors(result);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (loadingProject || loadingCategories || loadingTeams) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ type: "spring", duration: 2 }}
        dir="rtl"
      >
        <form>
          <div className="border-none shadow-lg overflow-hidden rounded-md">
            {/* Card header */}
            <div className="bg-gradient-to-r from-secondaryColor/20 to-transparent p-4">
              <Skeleton className="h-6 w-40" /> {/* Card Title */}
              <Skeleton className="mt-2 h-4 w-64" /> {/* Card Description */}
            </div>

            {/* Card content */}
            <div className="p-6 space-y-6">
              <div className="grid gap-6 md:grid-cols-2">
                {/* Project Name field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Input field */}
                </div>

                {/* Category field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Select trigger */}
                </div>

                {/* Start Date field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* DatePicker */}
                </div>

                {/* End Date field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* DatePicker */}
                </div>

                {/* Status field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Select trigger */}
                </div>

                {/* Team field */}
                <div className="space-y-2">
                  <Skeleton className="h-4 w-24" /> {/* Label */}
                  <Skeleton className="h-10 w-full" /> {/* Select trigger */}
                </div>
              </div>

              {/* Progress slider */}
              <div className="space-y-4">
                <Skeleton className="h-4 w-32" /> {/* Label */}
                <Skeleton className="h-10 w-full" /> {/* Slider */}
              </div>

              {/* Review stars */}
              <div className="space-y-2 mt-8">
                <Skeleton className="h-4 w-24" /> {/* Label */}
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5" /> {/* Star 1 */}
                  <Skeleton className="h-5 w-5" /> {/* Star 2 */}
                  <Skeleton className="h-5 w-5" /> {/* Star 3 */}
                  <Skeleton className="h-5 w-5" /> {/* Star 4 */}
                  <Skeleton className="h-5 w-5" /> {/* Star 5 */}
                </div>
              </div>
            </div>

            {/* Card footer with action buttons */}
            <div className="flex justify-between p-6 bg-gradient-to-r from-secondaryColor/5 to-transparent border-t border-secondaryColor/10">
              <Skeleton className="h-10 w-24" /> {/* Cancel button */}
              <Skeleton className="h-10 w-40" /> {/* Submit button */}
            </div>
          </div>
        </form>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ type: "spring", duration: 2 }}
      dir="rtl"
    >
      <form onSubmit={handleSubmit}>
        <Card className="border-none shadow-lg overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-secondaryColor/20 to-transparent">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCancel}
                className="ml-2"
              >
                <ArrowRight className="h-5 w-5" />
              </Button>
              <div>
                <CardTitle className="text-xl flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-mainColor" />
                  معلومات المشروع
                </CardTitle>
                <CardDescription>
                  تعديل المعلومات الأساسية للمشورع
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6 space-y-6 mb-6">
            <div className="grid gap-6 md:grid-cols-2">
              {/* Project Name */}
              <div className="space-y-2">
                <label htmlFor="name">اسم المشروع</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="أدخل اسم المشروع"
                  className="focus-visible:ring-mainColor"
                  required
                />
                {errors?.name && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.name[0]}
                  </p>
                )}
              </div>

              {/* Category */}
              <div className="space-y-2">
                <label htmlFor="category_id">القسم</label>
                <Select
                  disabled={loadingCategories}
                  value={formData.category_id}
                  onValueChange={(value) =>
                    handleSelectChange("category_id", value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر قسم المشروع" />
                  </SelectTrigger>
                  <SelectContent>
                    {Categories.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.category_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.category_id[0]}
                  </p>
                )}
              </div>

              {/* Start Date */}
              <div className="space-y-2">
                <label htmlFor="date_start">تاريخ البدء</label>
                <DatePicker
                  className="w-full rounded-md p-2"
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="اختر تاريخ ووقت البدء"
                  value={dayjs(formData.date_start)}
                  onChange={handleStartDateChange}
                />
                {errors?.date_start && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.date_start[0]}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div className="space-y-2">
                <label htmlFor="date_end">تاريخ الانتهاء</label>
                <DatePicker
                  className="w-full rounded-md p-2"
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="اختر تاريخ ووقت الانتهاء"
                  value={dayjs(formData.date_end)}
                  onChange={handleEndDateChange}
                />
                {errors?.date_end && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.date_end[0]}
                  </p>
                )}
              </div>

              {/* Status */}
              <div className="space-y-2">
                <label htmlFor="status">حالة المشروع</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange("status", value)}
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر حالة المشروع" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in_progress">قيد التنفيذ</SelectItem>
                    <SelectItem value="pending">قيد الانتظار</SelectItem>
                    <SelectItem value="completed">مكتمل</SelectItem>
                  </SelectContent>
                </Select>
                {errors?.status && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.status[0]}
                  </p>
                )}
              </div>

              {/* Team */}
              <div className="space-y-2">
                <label htmlFor="team_id">الفريق</label>
                <Select
                  disabled={loadingTeams}
                  value={formData.team_id}
                  onValueChange={(value) =>
                    handleSelectChange("team_id", value)
                  }
                >
                  <SelectTrigger className="focus:ring-mainColor">
                    <SelectValue placeholder="اختر فريق المشروع" />
                  </SelectTrigger>
                  <SelectContent>
                    {Teams.map((team) => (
                      <SelectItem key={team.id} value={team.id}>
                        {team.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors?.team_id && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.team_id[0]}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Progress */}
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <label htmlFor="progress">
                    نسبة الإنجاز ({formData.progress}%)
                  </label>
                </div>
                <Slider
                  marks={{
                    0: "0%",
                    25: "25%",
                    50: "50%",
                    75: "75%",
                    100: {
                      style: {
                        color: "#22c55e",
                      },
                      label: <strong>100%</strong>,
                    },
                  }}
                  defaultValue={formData.progress}
                  onChange={handleProgressChange}
                />
                {errors?.progress && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.progress[0]}
                  </p>
                )}
              </div>

              {/* Review */}
              <div className="space-y-2 ">
                <label htmlFor="review">التقييم</label>
                <div className="flex items-center gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      onClick={() => handleReviewChange(star.toString())}
                      key={star}
                      className={`w-5 h-5 cursor-pointer ${
                        formData.review >= star
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-gray-300"
                      }`}
                    />
                  ))}
                </div>
                {errors?.review && (
                  <p className="text-red-500 font-semibold text-sm">
                    {errors.review[0]}
                  </p>
                )}
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex justify-between p-6 bg-gradient-to-r from-secondaryColor/5 to-transparent border-t border-secondaryColor/10">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              className="hover:bg-secondaryColor/10 hover:text-mainColor"
            >
              إلغاء
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="bg-mainColor hover:bg-secondaryColor text-white"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  جاري الحفظ...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="h-4 w-4" />
                  تعديل المشروع
                </span>
              )}
            </Button>
          </CardFooter>
        </Card>
      </form>
    </motion.div>
  );
}
